import React from "react";
import CreatableSelect from "react-select/creatable";

/**
 * Searchable dropdown that also lets the user create a new option inline
 * ("Add ‘…’"). Thin wrapper around react-select/creatable styled to match the
 * app's inputs (rounded-xl, slate border, flamingo focus ring).
 *
 * Props:
 *  - label / required: field label row (same look as the local Input/Select components)
 *  - value: the selected option VALUE (not the option object)
 *  - onChange(value|null): fired with the option value on select/clear
 *  - options: [{ value, label }]
 *  - onCreate(inputLabel): called when the user picks "Add ‘…’" — should create the
 *    entity and (typically) select it by calling onChange itself. May be async.
 *  - isMulti: multi-select mode; value is an array of values.
 *  - placeholder, isDisabled, isLoading, createLabel
 */
const CreatableCombobox = ({
  label,
  required,
  value,
  onChange,
  options = [],
  onCreate,
  isMulti = false,
  placeholder = "Search or add…",
  isDisabled = false,
  isLoading = false,
  createLabel = "Add",
}) => {
  const selected = isMulti
    ? options.filter((o) => (value || []).some((v) => String(v) === String(o.value)))
    : options.find((o) => String(o.value) === String(value)) || null;

  const styles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#f75828" : "#cbd5e1",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(247, 88, 40, 0.25)" : "none",
      "&:hover": { borderColor: state.isFocused ? "#f75828" : "#94a3b8" },
      minHeight: "42px",
      fontSize: "0.875rem",
      backgroundColor: state.isDisabled ? "#f8fafc" : "#fff",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.875rem",
      backgroundColor: state.isSelected ? "#f75828" : state.isFocused ? "#fff1ec" : "#fff",
      color: state.isSelected ? "#fff" : "#0f172a",
      ":active": { backgroundColor: "#fde3d9" },
    }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "0.875rem" }),
    menu: (base) => ({ ...base, borderRadius: "0.75rem", overflow: "hidden", zIndex: 60 }),
    menuPortal: (base) => ({ ...base, zIndex: 60 }),
    multiValue: (base) => ({ ...base, backgroundColor: "#fff1ec", borderRadius: "0.5rem" }),
    multiValueLabel: (base) => ({ ...base, color: "#c2410c", fontWeight: 500 }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#c2410c",
      ":hover": { backgroundColor: "#fde3d9", color: "#9a3412" },
    }),
  };

  return (
    <div>
      {label && (
        <label className="text-xs font-medium text-slate-600">
          {label} {required && <span className="text-flamingo-600">*</span>}
        </label>
      )}
      <div className={label ? "mt-1" : ""}>
        <CreatableSelect
          isMulti={isMulti}
          isClearable
          isDisabled={isDisabled}
          isLoading={isLoading}
          value={selected}
          options={options}
          placeholder={placeholder}
          onChange={(opt) =>
            isMulti ? onChange((opt || []).map((o) => o.value)) : onChange(opt ? opt.value : null)
          }
          onCreateOption={(inputValue) => onCreate && onCreate(inputValue.trim())}
          isValidNewOption={(inputValue) =>
            Boolean(onCreate) &&
            Boolean(inputValue.trim()) &&
            !options.some((o) => o.label.toLowerCase() === inputValue.trim().toLowerCase())
          }
          formatCreateLabel={(inputValue) => `${createLabel} “${inputValue}”`}
          noOptionsMessage={() => (onCreate ? "Type to add a new one" : "No matches")}
          menuPortalTarget={document.body}
          styles={styles}
        />
      </div>
    </div>
  );
};

export default CreatableCombobox;
